"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WEBSITE_URL_ERROR_TEXT = void 0;
exports.normalizeWebsiteUrl = normalizeWebsiteUrl;
exports.healStoredWebsite = healStoredWebsite;
exports.normalizeWebsiteUrlList = normalizeWebsiteUrlList;
const brand_fields_1 = require("./brand-fields");
const tlds_1 = require("./tlds");
/** One text per error, shared by the API's 400s and the web's inline errors. */
exports.WEBSITE_URL_ERROR_TEXT = {
    EMPTY: "Enter your brand's public website.",
    INVALID: 'Enter a valid web address, such as www.yourbrand.com.',
    TOO_LONG: 'That web address is too long.',
};
function urlConstructor() {
    const ctor = globalThis.URL;
    return typeof ctor === 'function' ? ctor : null;
}
let knownTlds = null;
function isKnownTld(label) {
    if (!knownTlds)
        knownTlds = new Set([...tlds_1.GENERIC_TLDS, ...tlds_1.COUNTRY_TLDS].map((tld) => tld.toLowerCase()));
    return knownTlds.has(label) || label.startsWith('xn--');
}
const SCHEME_PREFIX = /^[a-z][a-z0-9+.-]*:\/\//i;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;
const LABEL = /^[a-z0-9-]{1,63}$/;
const fail = (error) => ({ ok: false, error });
/**
 * Normalize to `https://<host><path><query>` and check the shape. In order:
 * 1. trim; empty → EMPTY; over BRAND_LIMITS.websiteUrl characters → TOO_LONG; inner whitespace → INVALID;
 * 2. no `scheme://` → `https://` in front;
 * 3. not a URL → INVALID;
 * 4. a scheme other than http or https → INVALID (the result is always https, spec:43);
 * 5. a username or password → INVALID (this also catches `mailto:a@b.com`);
 * 6. a port other than 80 or 443 → INVALID (the result has none);
 * 7. one trailing dot dropped from the host; then INVALID for an IPv4 or IPv6 literal, fewer than two
 *    labels, a label that is not `[a-z0-9-]{1,63}` or that starts or ends with '-', a host over 253
 *    characters, or a last label that is not a known top-level domain (tlds.ts) and not `xn--`;
 * 8. the fragment is dropped, and so is a bare "/" path. A result over the limit is TOO_LONG too.
 */
function normalizeWebsiteUrl(input) {
    const text = typeof input === 'string' ? input.trim() : '';
    if (text === '')
        return fail('EMPTY');
    if (text.length > brand_fields_1.BRAND_LIMITS.websiteUrl)
        return fail('TOO_LONG');
    if (/\s/.test(text))
        return fail('INVALID');
    const URLCtor = urlConstructor();
    if (!URLCtor)
        return fail('INVALID');
    let parsed;
    try {
        parsed = new URLCtor(SCHEME_PREFIX.test(text) ? text : `https://${text}`);
    }
    catch {
        return fail('INVALID');
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
        return fail('INVALID');
    if (parsed.username !== '' || parsed.password !== '')
        return fail('INVALID');
    if (parsed.port !== '' && parsed.port !== '80' && parsed.port !== '443')
        return fail('INVALID');
    const host = parsed.hostname.toLowerCase().replace(/\.$/, '');
    if (host.startsWith('[') || IPV4.test(host))
        return fail('INVALID');
    if (host.length === 0 || host.length > 253)
        return fail('INVALID');
    const labels = host.split('.');
    if (labels.length < 2)
        return fail('INVALID');
    if (labels.some((label) => !LABEL.test(label) || label.startsWith('-') || label.endsWith('-'))) {
        return fail('INVALID');
    }
    if (!isKnownTld(labels[labels.length - 1]))
        return fail('INVALID');
    const path = parsed.pathname === '/' ? '' : parsed.pathname;
    const url = `https://${host}${path}${parsed.search}`;
    if (url.length > brand_fields_1.BRAND_LIMITS.websiteUrl)
        return fail('TOO_LONG');
    return { ok: true, url, host };
}
/** For a stored value: the healed URL when it normalizes, else the stored text with valid = false. */
function healStoredWebsite(stored) {
    if (typeof stored !== 'string' || stored.trim() === '')
        return { url: null, valid: false };
    const result = normalizeWebsiteUrl(stored);
    return result.ok ? { url: result.url, valid: true } : { url: stored, valid: false };
}
/**
 * The Release 4 scan list: normalize each entry, drop duplicates after normalizing, keep at most `max`
 * (BRAND_LIMITS.urls). Blank entries (an empty pasted line) are skipped without a rejection.
 */
function normalizeWebsiteUrlList(inputs, max) {
    const urls = [];
    const rejected = [];
    for (const input of Array.isArray(inputs) ? inputs : []) {
        if (typeof input !== 'string' || input.trim() === '')
            continue;
        const result = normalizeWebsiteUrl(input);
        if (!result.ok)
            rejected.push({ input, error: result.error });
        else if (urls.includes(result.url))
            rejected.push({ input, error: 'DUPLICATE' });
        else if (urls.length >= max)
            rejected.push({ input, error: 'TOO_MANY' });
        else
            urls.push(result.url);
    }
    return { urls, rejected };
}
//# sourceMappingURL=website-url.js.map